/**
 * Module entitlement: row shapes and the pure derivations the back office uses.
 *
 * Split from elevateEntitlementsApi.ts for the same reason the governance
 * modules are split — these are testable without a configured Supabase client.
 *
 * THE LIVE VERDICT IS NOT COMPUTED HERE. `is_live` and `live_reason` come from
 * public.module_entitlement_overview, which asks public.has_module — the same
 * function every RLS policy in the elevate schema goes through. Before HP-5B
 * the airlines screen recomputed `enabled && valid_from <= now && valid_until >
 * now` in JavaScript, which is a second implementation of MOD-01 that no test
 * could hold against the data and that a clock skew or a missed edge would put
 * quietly out of step. The derivations below are presentation only.
 */

export interface ModuleEntitlementRow {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  module: string;
  has_record: boolean;
  enabled: boolean;
  valid_from: string | null;
  valid_until: string | null;
  /** public.has_module's answer, not a re-derivation. */
  is_live: boolean;
  /** 'live' | 'disabled' | 'window has not started' | 'window has lapsed' | 'no entitlement record' */
  live_reason: string;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  updated_by_email: string | null;
}

export interface EntitlementAuditRow {
  event_id: string;
  occurred_at: string;
  tenant_id: string;
  tenant_name: string | null;
  module: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  enabled_before: boolean | null;
  valid_from_before: string | null;
  valid_until_before: string | null;
  enabled_after: boolean | null;
  valid_from_after: string | null;
  valid_until_after: string | null;
  /** The entitlement record did not exist before this event. */
  is_creation: boolean;
}

/** What the validity window is, in one short phrase, or null where unbounded. */
export function describeWindow(from: string | null, until: string | null): string | null {
  if (!from && !until) return null;
  const f = from ? from.slice(0, 10) : null;
  const u = until ? until.slice(0, 10) : null;
  if (f && u) return `${f} to ${u}`;
  if (f) return `from ${f}`;
  return `until ${u}`;
}

/**
 * The one line an audit row becomes. Written here rather than in JSX so the
 * before/after wording is asserted by a test: a history nobody can read
 * precisely is not evidence.
 */
export function describeAuditChange(row: EntitlementAuditRow): string {
  const after = row.enabled_after ? 'enabled' : 'disabled';
  const window = describeWindow(row.valid_from_after, row.valid_until_after);
  const suffix = window ? `, window ${window}` : ', no window';

  if (row.is_creation) return `created ${after}${suffix}`;

  const before = row.enabled_before ? 'enabled' : 'disabled';
  const beforeWindow = describeWindow(row.valid_from_before, row.valid_until_before);
  const windowChanged = beforeWindow !== window;

  if (before === after && !windowChanged) return `re-saved ${after}${suffix}`;
  if (before === after) return `${after}, window ${beforeWindow ?? 'none'} → ${window ?? 'none'}`;
  return `${before} → ${after}${suffix}`;
}

/** Sort order for the estate table: live first, then recorded, then the rest. */
export function sortEstate(rows: ModuleEntitlementRow[]): ModuleEntitlementRow[] {
  const rank = (r: ModuleEntitlementRow) => (r.is_live ? 0 : r.has_record ? 1 : 2);
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.tenant_name.localeCompare(b.tenant_name));
}
